import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const category = params.get("category") || "all";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "newest";
  const maxPrice = params.get("max") || "";
  const [term, setTerm] = useState(q);

  useEffect(() => setTerm(q), [q]);
  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: { category, q: q || undefined, sort, max_price: maxPrice || undefined } }).then((r) => setProducts(r.data));
  }, [category, q, sort, maxPrice]);

  const set = (k, v) => { const n = new URLSearchParams(params); v ? n.set(k, v) : n.delete(k); setParams(n); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-2">The ABS store</p>
      <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl">{CATEGORIES.find((c) => c.id === category)?.label || "All"} gear</h1>

      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center justify-between border-y border-white/10 py-5">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.id} data-testid={`category-filter-${c.id}`} onClick={() => set("category", c.id === "all" ? "" : c.id)}
              className={`px-4 h-10 font-display uppercase font-bold tracking-wider text-sm border transition-colors ${category === c.id ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-white/15 text-neutral-300 hover:text-white hover:border-white/40"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <form onSubmit={(e) => { e.preventDefault(); set("q", term); }} className="flex items-center border border-white/15 px-3 h-10 focus-within:border-[#FF3B30]">
            <Search className="w-4 h-4 text-neutral-500" />
            <input data-testid="shop-search-input" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search" className="bg-transparent outline-none text-sm px-2 w-36" />
          </form>
          <Select value={maxPrice || "any"} onValueChange={(v) => set("max", v === "any" ? "" : v)}>
            <SelectTrigger data-testid="price-filter" className="w-36 h-10 rounded-none border-white/15"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121215] border-white/10">
              <SelectItem value="any">Any price</SelectItem>
              <SelectItem value="20">Under $20</SelectItem>
              <SelectItem value="40">Under $40</SelectItem>
              <SelectItem value="70">Under $70</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="w-40 h-10 rounded-none border-white/15"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121215] border-white/10">
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p data-testid="results-count" className="font-mono text-xs text-neutral-500 mt-6 mb-6">{products ? `${products.length} PRODUCTS` : "LOADING..."}{q && ` FOR "${q.toUpperCase()}"`}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products?.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        {!products && Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-white/5 animate-pulse" />)}
      </div>
      {products?.length === 0 && <p data-testid="no-results" className="text-neutral-400 py-20 text-center">No gear matches that. Try another search.</p>}
    </div>
  );
}
